import React, { useState } from 'react';

import { genderType } from 'apis/handler/auth/authSchema';
import { usePostSignup } from 'apis/handler/auth/usePostSignup';
import Button from 'components/button/Button';
import FixedLoading from 'components/common/FixedLoading';
import InputContainer from 'components/input/InputContainer';
import ContentModalHeader from 'components/modal/ContentModalHeader';
import { getStoredReferralCode } from 'entities/referral';
import { SignupForm } from 'pages/login/SignupModal';
import { useTranslation } from 'react-i18next';
import { REFERRAL_REWARDS_ENABLED } from 'shared/constants';
import { convertOccupationToRequest, convertVisaInfoToRequest } from 'shared/lib';
import { isValidPassword, removeWhitespace, sha256 } from 'shared/lib';
import { useInfoStore } from 'store/useInfoStore';

import { trackSignupFieldError } from '../../lib';
import SignupConsents, { INITIAL_SIGNUP_CONSENTS, hasRequiredSignupConsents } from './SignupConsents';

interface SignupStep4Props {
  form: SignupForm;
  onInputChange: (field: string, value: string) => void;
  onClickNext: () => void;
}

const SignupStep4 = ({ onClickNext, form, onInputChange }: SignupStep4Props) => {
  const { t } = useTranslation('auth');
  const { setInfo, setProfileCompleted } = useInfoStore();
  const [checkPassword, setCheckPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordCheckError, setPasswordCheckError] = useState('');
  const [consents, setConsents] = useState(INITIAL_SIGNUP_CONSENTS);

  const { mutateAsync: trySignup, isPending } = usePostSignup({
    onSuccess: (data) => {
      // 사용자 정보 저장
      setInfo({
        accessToken: data.accessToken,
        email: data.email,
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        userId: `${data.id}`,
        memberRole: data.memberRole,
      });
      setProfileCompleted(true);

      if (window.dataLayer) {
        const today = new Date();
        const signupDate =
          today.getFullYear() +
          '-' +
          String(today.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(today.getDate()).padStart(2, '0');

        // sha256이 비동기(Web Crypto)라 해시 완료 후 이벤트를 push (해시는 1ms 내 완료)
        sha256(form.email)
          .catch(() => '') // 해시 실패 시에도 이벤트는 user_id 없이 push
          .then((hashedEmail) => {
            window.dataLayer?.push({
              event: 'sign_up',
              method: 'email',
              user_signup_date: signupDate,
              user_gender: form.gender,
              user_nationality: form.nationality,
              user_occupation: form.occupation,
              user_visa: form.visaInfo,
              user_status: 'active',
              user_id: hashedEmail,
            });
          });
      }

      onClickNext();
    },
    onError: (error) => {
      alert(error);
    },
  });

  const onChangePassword = (value: string) => {
    setPasswordError('');
    onInputChange('password', removeWhitespace(value));
  };

  const onChangeCheckPassword = (value: string) => {
    setPasswordCheckError('');
    setCheckPassword(removeWhitespace(value));
  };

  const handleNext = async () => {
    if (isPending) return;

    if (form.password === '' || !isValidPassword(form.password)) {
      setPasswordError(t('signup.step4.passwordRequirementError'));
      trackSignupFieldError(4, 'password');
      return;
    }

    if (checkPassword === '' || checkPassword !== form.password) {
      setPasswordCheckError(t('signup.step4.passwordMismatchError'));
      trackSignupFieldError(4, 'password_confirm');
      return;
    }

    if (!hasRequiredSignupConsents(consents)) {
      trackSignupFieldError(4, 'agreement');
      alert(t('signup.step4.agreementRequiredError'));
      return;
    }

    try {
      await trySignup({
        body: {
          email: form.email,
          password: form.password,
          // 서버는 영문으로 시작하는 이름만 받는다 — 앞뒤 공백은 떼고 보낸다
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          genderType: form.gender as genderType,
          birth: form.birthday,
          phoneNumber: form.phoneNumber,
          countryCode: removeWhitespace(form.countryCode, 'NUM'),
          nationality: form.nationality,
          occupationType: convertOccupationToRequest(form.occupation),
          visaType: convertVisaInfoToRequest(form.visaInfo),
          marketingConsent: consents.marketing,
          preferredLanguageCode: form.preferredLanguageCode,
          // 추천 링크로 유입돼 저장된 코드가 있으면 조용히 계정에 연결 (유저 입력 없음)
          referralCode: REFERRAL_REWARDS_ENABLED ? (getStoredReferralCode() ?? undefined) : undefined,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex w-[320px] max-w-full flex-col gap-y-4 px-5 py-5 md:mx-45 md:my-6 md:gap-y-5 md:p-0">
      <div className="flex w-full flex-col gap-y-5">
        <ContentModalHeader
          src="/images/optimized/illusts/il_key.webp"
          title={t('signup.title')}
          description={t('signup.step4.passwordDescription')}
        />
        <div className="flex w-full flex-col gap-y-0.5">
          <InputContainer
            type="password"
            label={t('signup.step4.createPasswordLabel')}
            placeholder={t('signup.step4.passwordPlaceholder')}
            error={passwordError}
            value={form.password}
            onChange={(e) => onChangePassword(e.target.value)}
          />
          <InputContainer
            type="password"
            label={t('signup.step4.confirmPasswordLabel')}
            placeholder={t('signup.step4.passwordPlaceholder')}
            error={passwordCheckError}
            value={checkPassword}
            onChange={(e) => onChangeCheckPassword(e.target.value)}
          />
        </div>

        {/* TODO: 체크박스 컴포넌트 shadcn으로 변경 */}
        <SignupConsents value={consents} onChange={setConsents} className="-mt-4" />
      </div>
      <Button
        text={t('signup.step4.continue')}
        shape="SQUARE"
        onClick={handleNext}
        disabled={isPending || !hasRequiredSignupConsents(consents)}
      />
      {isPending && <FixedLoading />}
    </div>
  );
};

export default SignupStep4;
